import {useApp} from "../../app.context";
import {useAccess} from "../../utils/useAccess";
import {useEffect, useMemo, useRef, useState} from "react";
import NumberInput from "../../elements/numberInput.element";
import {useIntegrator} from "../../integrator.context";
import {AddImageCommand} from "../../commands/addImage.command";
import {RemoveImageCommand} from "../../commands/removeImage.command";
import {UpdateImageCommand} from "../../commands/updateImage.command";
import UploadButton from "../../elements/uploadButton.element";
import PivotPreview from "../../elements/pivotPreview.element";
import WorkshopView from "./workshop.view";
import {clampScale, effectiveScale, SCALE_MAX, SCALE_MIN} from "../../utils/imageScale";
import {computePixelMultiplier} from "../../utils/exportTransform";
import {applyImagePackTidy} from "../../utils/imagePackTidy";
import {ChangeConstantsCommand} from "../../commands/changeConstants.command";
import AssetPackGrid from "../general/assetPackGrid.view";
import ConstructionImages from "./constructionImages";
import TintMaskDialog from "./tintMaskDialog";
import {deriveBoundsFromUrl, NormalisedBounds} from "../../utils/opaqueBounds";
import {ImageMetadata} from "../../models/remoteConstants.model";
import {
    classifyImageKeys,
    ClassifiedImage,
    MissingImage,
    missingImages,
    ImageSectionId,
    SECTION_ORDER,
    SECTION_TITLES,
} from "../../utils/imageClassification";


function ImagesEditorView() {
    const {state, executeCommand} = useIntegrator();
    const {setNotification, getImageUrl, getLocalizationValue, confirm, removeBackground, uploadFile,
        selectedImageKey: selectedKey, setSelectedImageKey: setSelectedKey, attributeManifest} = useApp();
    const {canServerWrite} = useAccess();
    const [newKey, setNewKey] = useState<string>('');
    const [previewImage, setPreviewImage] = useState<string | undefined>(undefined);
    // Image whose team-colour mask is being authored, if any.
    const [maskingImage, setMaskingImage] = useState<string | undefined>(undefined);
    const [collapsed, setCollapsed] = useState<Set<ImageSectionId | 'missing'>>(new Set());
    const [showViewPrefs, setShowViewPrefs] = useState(false);
    const viewPrefsRef = useRef<HTMLDivElement>(null);
    // Image key being dragged from the grid (HTML5 dnd) — lets the Workshop
    // render a snap ghost during dragover, when dataTransfer is sealed.
    const [draggingImageKey, setDraggingImageKey] = useState<string | undefined>(undefined);
    // In-flight pivot while dragging the flyout crosshair — Workshop
    // instances preview it live; the command only commits on release.
    const [transientPivot, setTransientPivot] = useState<{key: string; x: number; y: number} | undefined>(undefined);
    // Same idea for the Scale slider: live preview while sliding, one
    // UpdateImageCommand on release.
    const [transientScale, setTransientScale] = useState<{key: string; value: number} | undefined>(undefined);
    // Raw text of the Scale number field while it's being typed (lets the
    // field hold partial/empty input without snapping back). Undefined when
    // not editing — the field then mirrors the live value.
    const [scaleDraft, setScaleDraft] = useState<string | undefined>(undefined);
    // Natural pixel dimensions of the selected image, for the Details panel
    // (and to show the PPU the export will bake). Browser-cached, so this reuses
    // the preview's image load.
    const [naturalDims, setNaturalDims] = useState<{w: number; h: number} | undefined>(undefined);
    // Subject bounds derived live from the selected image's pixels — what the
    // client would compute for itself when the record has none stored.
    const [derivedBounds, setDerivedBounds] = useState<NormalisedBounds | undefined>(undefined);
    // Bounds already derived this session, by image key. Read synchronously when
    // an edit is committed so the bounds can ride along in the SAME command —
    // deriving at that moment would need an await, and a second command would cost
    // the user a second undo press for one action.
    const boundsCacheRef = useRef(new Map<string, NormalisedBounds>());
    const [detailsOpen, setDetailsOpen] = useState(true);

    // Layout: the Workshop owns the main area; a fixed-width sidebar on the
    // right stacks the images grid (top) over the image-details pane (bottom).
    // Both splits are drag-resizable and persisted.

    // Images-grid height (px) within the sidebar — the details pane takes the rest.
    const IMAGES_HEIGHT_KEY = 'rift.editor.imagesGridHeight';
    const IMAGES_MIN_HEIGHT = 200;
    const DETAILS_MIN_HEIGHT = 160;
    // Dragging a resize handle to within this many px of the panel's far edge
    // collapses it, restoring to the size captured when the handle was grabbed.
    const HIDE_EDGE_SLOP = 8;
    const sidebarRef = useRef<HTMLDivElement>(null);
    // Scrollable thumbnail grid — used to bring a newly-selected image into view.
    const gridScrollRef = useRef<HTMLDivElement>(null);
    const imagesDragStartRef = useRef<number | null>(null);
    const [imagesHeight, _setImagesHeight] = useState<number>(() => {
        const stored = Number(localStorage.getItem(IMAGES_HEIGHT_KEY));
        return Number.isFinite(stored) && stored >= IMAGES_MIN_HEIGHT ? stored : 360;
    });
    function setImagesHeight(h: number) {
        localStorage.setItem(IMAGES_HEIGHT_KEY, String(h));
        _setImagesHeight(h);
    }

    // Sidebar width (persisted), draggable against the Workshop.
    const rootRef = useRef<HTMLDivElement>(null);
    const SIDEBAR_WIDTH_KEY = 'rift.editor.imagesSidebarWidth';
    const SIDEBAR_MIN_WIDTH = 280;
    const WORKSHOP_MIN_WIDTH = 320;
    const [sidebarWidth, _setSidebarWidth] = useState<number>(() => {
        const stored = Number(localStorage.getItem(SIDEBAR_WIDTH_KEY));
        return Number.isFinite(stored) && stored >= SIDEBAR_MIN_WIDTH ? stored : 440;
    });
    const sidebarWidthRef = useRef(sidebarWidth);

    // Image-details pane toggle (persisted). When shown it's always present —
    // blank when no image is selected; collapsed, the grid takes the column.
    const DETAILS_SHOWN_KEY = 'rift.editor.imageFlyoutShown';
    const [showDetails, _setShowDetails] = useState<boolean>(
        () => localStorage.getItem(DETAILS_SHOWN_KEY) !== 'false');
    function setShowDetails(v: boolean) {
        localStorage.setItem(DETAILS_SHOWN_KEY, String(v));
        _setShowDetails(v);
    }

    // Resizable image-preview height within the flyout (drag the divider between
    // the preview and the controls below it). Persisted; clamped 32–512.
    const PREVIEW_HEIGHT_KEY = 'rift.editor.imagePreviewHeight';
    const PREVIEW_MIN_HEIGHT = 32;
    const PREVIEW_MAX_HEIGHT = 512;
    const [previewHeight, _setPreviewHeight] = useState<number>(() => {
        const stored = Number(localStorage.getItem(PREVIEW_HEIGHT_KEY));
        return Number.isFinite(stored) && stored >= PREVIEW_MIN_HEIGHT
            ? Math.min(PREVIEW_MAX_HEIGHT, stored) : 240;
    });
    function setPreviewHeight(h: number) {
        const clamped = Math.max(PREVIEW_MIN_HEIGHT, Math.min(PREVIEW_MAX_HEIGHT, h));
        localStorage.setItem(PREVIEW_HEIGHT_KEY, String(clamped));
        _setPreviewHeight(clamped);
    }
    const previewDragRef = useRef<{startY: number; startHeight: number} | null>(null);
    function onPreviewResizePointerDown(e: React.PointerEvent<HTMLDivElement>) {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        previewDragRef.current = {startY: e.clientY, startHeight: previewHeight};
    }
    function onPreviewResizePointerMove(e: React.PointerEvent<HTMLDivElement>) {
        const drag = previewDragRef.current;
        if (!drag || !e.currentTarget.hasPointerCapture(e.pointerId)) return;
        setPreviewHeight(drag.startHeight + (e.clientY - drag.startY));
    }
    function onPreviewResizePointerUp() {
        previewDragRef.current = null;
    }

    // Width resize: delta-based off the grab point so the grip stays exactly
    // under the cursor (no snap on grab, no drift on vertical-only motion).
    // Pointer capture keeps tracking even off the thin handle; updates coalesce
    // to one per animation frame and persist only on release.
    const sidebarDragRef = useRef<{pointerId: number; startX: number; startWidth: number; clientX: number; raf: number | null} | null>(null);
    function applySidebarWidth() {
        const drag = sidebarDragRef.current;
        const root = rootRef.current;
        if (drag) drag.raf = null;
        if (!drag || !root) return;
        const bounds = root.getBoundingClientRect();
        const raw = drag.startWidth - (drag.clientX - drag.startX); // drag left widens
        const max = Math.max(SIDEBAR_MIN_WIDTH, bounds.width - WORKSHOP_MIN_WIDTH);
        const w = Math.round(Math.min(max, Math.max(SIDEBAR_MIN_WIDTH, raw)));
        sidebarWidthRef.current = w;
        _setSidebarWidth(w);
    }
    function onSidebarResizePointerDown(e: React.PointerEvent<HTMLDivElement>) {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        sidebarDragRef.current = {
            pointerId: e.pointerId, startX: e.clientX,
            startWidth: sidebarWidthRef.current, clientX: e.clientX, raf: null,
        };
    }
    function onSidebarResizePointerMove(e: React.PointerEvent<HTMLDivElement>) {
        const drag = sidebarDragRef.current;
        if (!drag || drag.pointerId !== e.pointerId) return;
        drag.clientX = e.clientX;
        if (drag.raf === null) drag.raf = requestAnimationFrame(applySidebarWidth);
    }
    function onSidebarResizePointerUp() {
        const drag = sidebarDragRef.current;
        if (!drag) return;
        if (drag.raf !== null) cancelAnimationFrame(drag.raf);
        sidebarDragRef.current = null;
        localStorage.setItem(SIDEBAR_WIDTH_KEY, String(sidebarWidthRef.current));
    }

    // Commit a pivot edited by Ctrl-dragging in the Workshop (one history
    // entry). Clears the live transient regardless; only writes when the
    // value actually changed.
    function commitWorkshopPivot(key: string, x: number, y: number) {
        setTransientPivot(undefined);
        const value = state.images[key];
        if (!value) return;
        if (value.offsetX === x && value.offsetY === y) return;
        value.offsetX = x;
        value.offsetY = y;
        stampBounds(key, value);
        executeCommand(new UpdateImageCommand(key, value));
    }

    // Commit a scale edited by Alt-dragging in the Workshop. Same contract.
    function commitWorkshopScale(key: string, scale: number) {
        setTransientScale(undefined);
        const value = state.images[key];
        if (!value) return;
        const clamped = clampScale(scale);
        if ((value.scale ?? 1) === clamped) return;
        value.scale = clamped;
        stampBounds(key, value);
        executeCommand(new UpdateImageCommand(key, value));
    }

    function onSeparatorPointerDown(e: React.PointerEvent<HTMLDivElement>) {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        imagesDragStartRef.current = imagesHeight;  // restore size if collapsed
    }

    function onSeparatorPointerMove(e: React.PointerEvent<HTMLDivElement>) {
        if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
        const container = sidebarRef.current;
        if (!container) return;
        const bounds = container.getBoundingClientRect();
        // Dragged all the way to the bottom edge → collapse the details pane,
        // keeping the grid height the separator was grabbed at so re-showing it
        // restores the same split.
        if (e.clientY >= bounds.bottom - HIDE_EDGE_SLOP) {
            setImagesHeight(imagesDragStartRef.current ?? imagesHeight);
            setShowDetails(false);
            e.currentTarget.releasePointerCapture(e.pointerId);
            return;
        }
        const raw = e.clientY - bounds.top - 3; // 3 = half separator height
        const max = Math.max(IMAGES_MIN_HEIGHT, bounds.height - DETAILS_MIN_HEIGHT);
        setImagesHeight(Math.round(Math.min(max, Math.max(IMAGES_MIN_HEIGHT, raw))));
    }

    // Viewing preferences (thumbnail size + alpha-checkerboard appearance),
    // persisted as one blob like the sidebar/tab state. Defaults match the
    // .image-cell CSS so an untouched editor looks exactly as before.
    interface ImageViewPrefs {
        thumbSize: number;
        gridSize: number;
        gridOpacity: number;   // 0..100, applied to the gridColorB squares
        gridColorA: string;    // base fill
        gridColorB: string;    // checker squares drawn over the base
    }
    const VIEW_PREFS_KEY = 'rift.editor.imageViewPrefs';
    const DEFAULT_PREFS: ImageViewPrefs = {
        thumbSize: 115, gridSize: 16, gridOpacity: 100,
        gridColorA: '#2a2a2a', gridColorB: '#444444',
    };
    // Thumbnail size bounds (shared by the clamp + slider) and the label-font
    // scale at each end: label size ramps linearly with thumbnail size from
    // font-size-default × 0.8 at the smallest to × 1.2 at the largest.
    const THUMB_MIN = 72;
    const THUMB_MAX = 128;
    const LABEL_SCALE_MIN = 0.8;
    const LABEL_SCALE_MAX = 1.2;
    const [prefs, _setPrefs] = useState<ImageViewPrefs>(() => {
        try {
            const stored = JSON.parse(localStorage.getItem(VIEW_PREFS_KEY) ?? '{}');
            // Older blobs stored the base as `bgColor`.
            if (stored.bgColor && !stored.gridColorA) stored.gridColorA = stored.bgColor;
            // Snap pre-quantization grid sizes to the slider's step.
            const grid = stored.gridSize ?? DEFAULT_PREFS.gridSize;
            stored.gridSize = Math.min(64, Math.max(8, Math.round(grid / 8) * 8));
            return {...DEFAULT_PREFS, ...stored, thumbSize: Math.max(THUMB_MIN, stored.thumbSize ?? DEFAULT_PREFS.thumbSize)};
        } catch {
            return DEFAULT_PREFS;
        }
    });
    const setPrefs = (value: Partial<ImageViewPrefs>) => {
        const next = {...prefs, ...value};
        localStorage.setItem(VIEW_PREFS_KEY, JSON.stringify(next));
        _setPrefs(next);
    };
    const thumbSize = prefs.thumbSize;
    // Linear ramp of the label font scale across the thumbnail-size range.
    const labelScale = LABEL_SCALE_MIN + (LABEL_SCALE_MAX - LABEL_SCALE_MIN)
        * Math.min(1, Math.max(0, (thumbSize - THUMB_MIN) / (THUMB_MAX - THUMB_MIN)));

    // Inline background overriding the .image-cell defaults — also applied to
    // the flyout preview so both render sprites against the same backdrop.
    // Single repeating-conic-gradient (not the offset dual-45°-gradient
    // trick, which produces seams/doubled rows at many sizes), with the grid
    // size quantized to multiples of 8 so squares stay on whole pixels.
    const checkerStyle: React.CSSProperties = useMemo(() => {
        const hex = prefs.gridColorB.replace('#', '');
        const [r, g, b] = [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16));
        const square = `rgba(${r}, ${g}, ${b}, ${prefs.gridOpacity / 100})`;
        return {
            backgroundColor: prefs.gridColorA,
            backgroundImage: `repeating-conic-gradient(${square} 0% 25%, transparent 0% 50%)`,
            backgroundSize: `${prefs.gridSize}px ${prefs.gridSize}px`,
        };
    }, [prefs]);

    // Names, sections, and usage are all DERIVED — see imageClassification.ts.
    const classification = useMemo(
        () => classifyImageKeys(state, getLocalizationValue, attributeManifest),
        [state, getLocalizationValue, attributeManifest]
    );

    const sections = useMemo(() => {
        const by = new Map<ImageSectionId, ClassifiedImage[]>();
        for (const info of Array.from(classification.values())) {
            const list = by.get(info.section) ?? [];
            list.push(info);
            by.set(info.section, list);
        }
        for (const list of Array.from(by.values()))
            list.sort((a, b) => a.label.localeCompare(b.label));
        return by;
    }, [classification]);

    // Images the Client will ask for that this branch lacks — the "Missing"
    // section under Unclassified. Not part of `classification`: there is no
    // image to classify.
    const missing = useMemo(
        () => missingImages(state, attributeManifest, getLocalizationValue),
        [state, attributeManifest, getLocalizationValue]
    );

    // Self-healing: if the selected key vanished (undo, branch switch, delete),
    // the flyout simply doesn't render.
    const selected = selectedKey !== undefined ? classification.get(selectedKey) : undefined;

    useEffect(() => {
        if (selectedKey === undefined) return;
        function onKey(e: KeyboardEvent) {
            // Don't steal Escape from anything layered over the flyout — the
            // fullscreen preview or the mask dialog own it while they're up.
            if (e.key === 'Escape' && previewImage === undefined && maskingImage === undefined) {
                setSelectedKey(undefined);
            }
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // setSelectedKey is a stable context setter; the rest are the real triggers.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedKey, previewImage, maskingImage]);

    // Load the selected image's natural dimensions for the Details panel.
    useEffect(() => {
        setNaturalDims(undefined);
        if (selectedKey === undefined) return;
        const img = new Image();
        img.onload = () => setNaturalDims({w: img.naturalWidth, h: img.naturalHeight});
        img.src = getImageUrl(selectedKey);
    }, [selectedKey, getImageUrl]);

    // Derive the subject bounds for the Details panel, so the box is visible for
    // every image rather than only the ones uploaded since it started being
    // recorded. A SEPARATE crossOrigin load, kept off the path above for the same
    // reason the Workshop keeps its sprite analysis apart: pixel reads depend on
    // CORS and must never hold up the plain preview.
    useEffect(() => {
        setDerivedBounds(undefined);
        if (selectedKey === undefined) return;

        let cancelled = false;
        const key = selectedKey;
        deriveBoundsFromUrl(getImageUrl(key)).then(bounds => {
            if (bounds) boundsCacheRef.current.set(key, bounds);
            if (!cancelled) setDerivedBounds(bounds);
        });
        return () => { cancelled = true; };
    }, [selectedKey, getImageUrl]);

    // Record an image's subject bounds as a side effect of editing it, so the field
    // fills in through ordinary authoring instead of needing a sweep over every
    // record. Mutates the value the caller is about to commit, so the bounds land
    // in that one command and undo stays a single press.
    //
    // Only ever FILLS IN: an image that already has bounds keeps them, because they
    // may have been deliberately adjusted to frame that art, and a scale or pivot
    // edit is no reason to overwrite that. A re-upload is the one thing that
    // recomputes them (see uploadFile), which is right — new art, new subject.
    //
    // Silently does nothing when this image's bounds haven't been derived yet. That
    // only happens for an image edited without ever being selected (a Workshop drag
    // of something else), and it kicks off the derivation so the next edit records
    // it. Nothing depends on the field existing — the client derives it either way.
    function stampBounds(key: string, value: ImageMetadata): void {
        if (value.trim) return;

        const cached = boundsCacheRef.current.get(key);
        if (cached) {
            value.trim = cached;
            return;
        }

        deriveBoundsFromUrl(getImageUrl(key)).then(bounds => {
            if (bounds) boundsCacheRef.current.set(key, bounds);
        });
    }

    // Scroll the selected image's thumbnail into view — but only as far as
    // needed (block/inline 'nearest' is a no-op when it's already visible, and
    // otherwise scrolls the minimum). A selected pack MEMBER has no thumbnail of
    // its own in the grid (the pack shows one representative), so target the
    // pack group via its owner instead.
    useEffect(() => {
        const container = gridScrollRef.current;
        if (!container || selectedKey === undefined) return;
        const cssAttr = (v: string) => v.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        const owner = classification.get(selectedKey)?.pack?.owner;
        const selector = owner
            ? `[data-pack-owner="${cssAttr(owner)}"]`
            : `[data-image-key="${cssAttr(selectedKey)}"]`;
        const el = container.querySelector(selector) as HTMLElement | null;
        el?.scrollIntoView({block: 'nearest', inline: 'nearest'});
    }, [selectedKey, classification]);

    // Dismiss the View Settings popup on a click outside it (the ref wraps both
    // the toggle button and the popup, so clicking the button just toggles).
    // Don't dismiss while a native colour picker is open — its <input type=color>
    // stays the focused element, and clicks in the OS dialog shouldn't close us.
    useEffect(() => {
        if (!showViewPrefs) return;
        function onPointerDown(e: PointerEvent) {
            if (viewPrefsRef.current?.contains(e.target as Node)) return;
            const active = document.activeElement;
            if (active instanceof HTMLInputElement && active.type === 'color') return;
            setShowViewPrefs(false);
        }
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [showViewPrefs]);

    function toggleSection(id: ImageSectionId | 'missing') {
        const next = new Set(collapsed);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setCollapsed(next);
    }

    function addNewImage() {
        if (newKey === '') {
            setNotification({
                title: 'Invalid Input',
                message: 'Please enter a valid key.'
            })
            return;
        }

        if (newKey in state.images) {
            setNotification({
                title: 'Key Already Exists',
                message: 'Please enter a new key.'
            })
            return;
        }

        executeCommand(new AddImageCommand(newKey));
        setNewKey('');
    }

    async function tidyImagePacks() {
        // Dry-run on a clone to preview what would change.
        const preview = applyImagePackTidy(structuredClone(state));
        if (preview.collapsedLevels === 0 && preview.archivedVestiges.length === 0) {
            setNotification({title: 'Nothing to tidy', message: 'Image packs are already clean.'});
            return;
        }
        const proceed = await confirm({
            title: 'Tidy image packs?',
            body: (
                <div className={'layout vertical gap'}>
                    <p>Cleans up legacy image-pack cruft. <strong>No in-game change</strong> — the
                        runtime renders the same images.</p>
                    {preview.collapsedLevels > 0 &&
                        <p className={'muted-text'}>• Collapse {preview.collapsedLevels} redundant
                            duplicate building level{preview.collapsedLevels === 1 ? '' : 's'}</p>}
                    {preview.archivedVestiges.length > 0 &&
                        <p className={'muted-text'}>• Archive {preview.archivedVestiges.length} unused
                            entity image{preview.archivedVestiges.length === 1 ? '' : 's'} (recoverable):{' '}
                            {preview.archivedVestiges.join(', ')}</p>}
                </div>
            ),
            confirmText: 'Tidy',
        });
        if (!proceed) return;
        executeCommand(new ChangeConstantsCommand(d => { applyImagePackTidy(d); }));
    }

    // A key the Client needs and this branch lacks: a dashed "?" cell. Clicking it
    // adds an empty image under that key (undoable) and selects it, ready for an
    // upload — the same entry the Add button makes.
    function renderMissing(entry: MissingImage) {
        const labelWidth = Math.max(thumbSize + 1, 48);
        const tooltip = `${entry.key} — not in this branch\n`
            + (entry.description ? `${entry.description}\n` : '')
            + entry.sources.map(source => `• ${source}`).join('\n')
            + '\nClick to add an image with this key';
        return (
            <div key={entry.key} className={'layout vertical center'} style={{width: labelWidth + 4}}>
                <div className={'image-cell layout vertical center'}
                     style={{
                         cursor: 'pointer',
                         justifyContent: 'center',
                         width: thumbSize, height: thumbSize,
                         boxSizing: 'border-box',
                         border: '1px dashed rgba(255,255,255,0.4)',
                     }}
                     onClick={() => {
                         executeCommand(new AddImageCommand(entry.key));
                         setSelectedKey(entry.key);
                     }}
                     title={tooltip}>
                    <span className={'fa-solid fa-question'}
                          style={{fontSize: thumbSize * 0.4, color: 'rgba(255,255,255,0.4)'}}/>
                </div>
                <p style={{
                    fontSize: `calc(var(--font-size-default) * ${labelScale})`,
                    boxSizing: 'border-box', maxWidth: labelWidth, width: labelWidth,
                    padding: '1px 2px',
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                    minHeight: '2.4em',
                    color: 'rgba(255,255,255,0.66)',
                }}
                   title={tooltip}>
                    {entry.key}
                </p>
            </div>
        );
    }

    function renderThumbnail(info: ClassifiedImage, corner?: React.ReactNode, labelText?: string, packOwner?: string) {
        const orphan = info.usedBy.length === 0;
        const isSelected = info.key === selectedKey;
        const tooltip = orphan
            ? `${info.label} (unused)\n${info.key}`
            : `${info.label}\n${info.key}\n` + info.usedBy.map(u => `• ${u.label}`).join('\n');
        // Labels stay readable at small thumbnail sizes by keeping a floor on
        // their width — cells just get a little wider than the image then.
        const labelWidth = Math.max(thumbSize + 1, 48);
        // A single-word label (no whitespace) can't wrap usefully, so it gets
        // truncated with an ellipsis on one line; multi-word labels still wrap
        // to two lines.
        const name = labelText ?? info.label;
        const singleWord = !/\s/.test(name.trim());
        return (
            <div key={info.key} className={'layout vertical center'} style={{width: labelWidth + 4}}
                 data-image-key={info.key} data-pack-owner={packOwner}>
                <div className={`image-cell${isSelected ? ' selected' : ''}`}
                     style={{
                         cursor: 'pointer',
                         position: 'relative',
                         width: thumbSize, height: thumbSize,
                         opacity: orphan ? 0.6 : 1,
                         ...checkerStyle,
                     }}
                     onClick={() => setSelectedKey(isSelected ? undefined : info.key)}
                     title={tooltip}>
                    <img alt={info.label} src={getImageUrl(info.key)}
                         draggable
                         onDragStart={e => {
                             e.dataTransfer.setData('text/plain', info.key);
                             e.dataTransfer.effectAllowed = 'copy';
                             setDraggingImageKey(info.key);
                             // Grabbing an image makes it the active flyout image.
                             setSelectedKey(info.key);
                         }}
                         onDragEnd={() => setDraggingImageKey(undefined)}
                         style={{width: '100%', height: '100%', objectFit: 'contain'}}/>
                    {corner}
                </div>
                <p style={{
                    fontSize: `calc(var(--font-size-default) * ${labelScale})`,
                    boxSizing: 'border-box', maxWidth: labelWidth, width: labelWidth,
                    padding: '1px 2px',
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    // Reserve two lines under every thumbnail so the grid stays
                    // even whether a label is one line or wraps to two. A single
                    // word truncates with an ellipsis; multi-word wraps via the
                    // two-line -webkit-box clamp.
                    display: singleWord ? 'block' : '-webkit-box',
                    WebkitLineClamp: singleWord ? undefined : 2,
                    WebkitBoxOrient: singleWord ? undefined : 'vertical',
                    whiteSpace: singleWord ? 'nowrap' : 'normal',
                    wordBreak: singleWord ? 'normal' : 'break-word',
                    lineHeight: 1.2,
                    minHeight: '2.4em',
                    fontStyle: orphan ? 'italic' : 'normal',
                    color: isSelected ? 'var(--tint-color)' : 'rgba(255,255,255,0.66)',
                }}
                   title={tooltip}>
                    {labelText ?? info.label}{orphan ? ' •' : ''}
                </p>
            </div>
        );
    }

    // A section renders a mix of standalone cards and asset-pack groups. A group
    // collapses to its representative (V1 L1) with a +/- toggle; expanded, it
    // becomes a bordered block whose members wrap inside (so the "surround"
    // is correct across rows).
    type RenderUnit =
        | {kind: 'single'; info: ClassifiedImage}
        | {kind: 'pack'; owner: string; rep: ClassifiedImage; members: ClassifiedImage[]};

    function buildRenderUnits(items: ClassifiedImage[]): RenderUnit[] {
        const groups = new Map<string, ClassifiedImage[]>();
        const units: RenderUnit[] = [];
        for (const info of items) {
            // Group an entity's pack slots AND a building's construction frames
            // under one owner, so construction images aren't loose tiles.
            const owner = info.pack?.owner ?? info.constructionOf?.owner;
            if (owner) {
                const list = groups.get(owner) ?? [];
                list.push(info);
                groups.set(owner, list);
            } else {
                units.push({kind: 'single', info});
            }
        }
        for (const [owner, list] of Array.from(groups.entries())) {
            // Pack slots first (by level/variation), then construction frames (by
            // index). Pack-less members have no level — don't deref pack!.
            list.sort((a, b) => {
                if (a.pack && b.pack)
                    return (a.pack.level - b.pack.level) || (a.pack.variation - b.pack.variation);
                if (a.pack) return -1;
                if (b.pack) return 1;
                return (a.constructionOf?.index ?? 0) - (b.constructionOf?.index ?? 0);
            });
            const rep = list.find(i => i.pack?.main) ?? list[0];
            units.push({kind: 'pack', owner, rep, members: list});
        }
        const labelOf = (u: RenderUnit) => u.kind === 'single' ? u.info.label : u.rep.label;
        units.sort((a, b) => labelOf(a).localeCompare(labelOf(b)));
        return units;
    }

    function renderPackGroup(unit: Extract<RenderUnit, {kind: 'pack'}>) {
        // One representative tile per pack, labelled by entity name. Selecting it
        // opens the flyout's Asset-Pack grid, where its level/variant images are
        // viewed and edited — no inline expansion in the grid any more.
        return renderThumbnail(unit.rep, undefined, getLocalizationValue(unit.owner), unit.owner);
    }

    function renderFlyout(info: ClassifiedImage) {
        const image = info.key;
        const value = state.images[image];
        const orphan = info.usedBy.length === 0;
        // For a building image (a pack slot or a construction frame), surface the
        // building's construction frames in the preview so they're managed here
        // rather than as loose tiles. (find() against state.buildings naturally
        // filters out non-building pack owners like units.)
        const flyoutBuildingId = info.pack?.owner ?? info.constructionOf?.owner;
        const flyoutBuilding = flyoutBuildingId
            ? state.buildings?.find(b => b.id === flyoutBuildingId)
            : undefined;
        const showConstruction = !!flyoutBuilding && (flyoutBuilding.constructionTurns ?? 0) > 0;
        // Show the entity's asset-pack grid for any pack image, and also for a
        // building's construction frames (info.pack is unset on a frame — fall
        // back to the building id) so both live in one navigable flyout.
        const flyoutPackId = info.pack?.owner ?? flyoutBuilding?.id;
        const shownScale = transientScale?.key === image ? transientScale.value : effectiveScale(value.scale);
        // Live preview as the slider/field changes — no command yet.
        function previewScale(n: number) {
            setTransientScale({key: image, value: clampScale(n)});
        }
        // Commit the in-flight value as a single history entry.
        function commitScale() {
            if (transientScale?.key !== image) return;
            value.scale = clampScale(transientScale.value);
            stampBounds(image, value);
            executeCommand(new UpdateImageCommand(image, value));
            setTransientScale(undefined);
        }
        // Text field uses its own draft while typing so partial/empty input
        // doesn't snap back; otherwise it mirrors the live value.
        const scaleFieldValue = scaleDraft !== undefined ? scaleDraft : shownScale.toFixed(2);
        function onScaleText(text: string) {
            setScaleDraft(text);
            const n = Number(text);
            if (text.trim() !== '' && Number.isFinite(n)) previewScale(n);
        }
        function commitScaleText() {
            const draft = scaleDraft;
            setScaleDraft(undefined);
            if (draft === undefined) {
                commitScale();   // slider edit, no text typed
                return;
            }
            const n = Number(draft);
            setTransientScale(undefined);
            // Commit only a valid, actually-changed value; otherwise revert
            // to the stored value (which the cleared draft now reveals).
            if (draft.trim() !== '' && Number.isFinite(n)) {
                const finalVal = clampScale(n);
                if (finalVal !== (value.scale ?? 1)) {
                    value.scale = finalVal;
                    stampBounds(image, value);
                    executeCommand(new UpdateImageCommand(image, value));
                }
            }
        }
        return (
            <div className={'layout vertical box'}
                 style={{flexGrow: 1, minWidth: 0, padding: 12, gap: 12, minHeight: 0,
                     position: 'relative',
                     transition: 'none'}}>{/* .box has transition:all 0.2s — would
                 animate size on every drag frame, making resize lag the cursor */}
                {/* Collapse button pinned to the pane's top-right so it stays
                    put regardless of the asset-pack section above. */}
                <div className={'layout horizontal'}
                     style={{position: 'absolute', top: 12, right: 12, gap: 6, zIndex: 1}}>
                    <button title={'Hide image details'} className={'panel-toggle'} onClick={() => setShowDetails(false)}>
                        <span className={'fa-solid fa-chevron-down'}/>
                    </button>
                </div>
                {/* Everything below scrolls as one column: the pane is only as
                    tall as the sidebar split leaves it, so the pack grid and the
                    fixed-height preview can easily exceed it. */}
                <div className={'layout vertical'}
                     style={{flex: '1 1 0', minHeight: 0, overflowY: 'auto', overflowX: 'hidden', gap: 12}}>
                {/* Asset-pack grid (only for images that belong to a pack): pick a
                    level/variant slot to view & edit it below; +/✕ manage slots. */}
                {flyoutPackId && state.imagePacks?.[flyoutPackId] &&
                    <div className={'layout vertical gap'} style={{flexShrink: 0, gap: 6}}>
                        <p className={'muted-text'} style={{fontSize: 12}}>
                            Asset pack — {getLocalizationValue(flyoutPackId)}
                        </p>
                        <AssetPackGrid packId={flyoutPackId} selectedKey={image}
                                       onSelectSlot={setSelectedKey}
                                       onDragImageStart={setDraggingImageKey}
                                       onDragImageEnd={() => setDraggingImageKey(undefined)}/>
                    </div>}
                {/* Construction frames for a building — grouped here (alongside the
                    pack) instead of floating as loose tiles in the section. */}
                {showConstruction &&
                    <div className={'layout vertical gap'} style={{flexShrink: 0, gap: 6}}>
                        <p className={'muted-text'} style={{fontSize: 12}}>
                            Construction — {getLocalizationValue(flyoutBuilding!.id)}
                        </p>
                        <ConstructionImages building={flyoutBuilding!} selectedKey={image}
                                            onSelectSlot={setSelectedKey}
                                            onDragImageStart={setDraggingImageKey}
                                            onDragImageEnd={() => setDraggingImageKey(undefined)}/>
                    </div>}
                {/* Header (title only) — the collapse button is pinned top-right
                    above; pad right so a long title/key clears it. */}
                <div className={'layout vertical'} style={{minWidth: 0, flexShrink: 0, paddingRight: 36}}>
                        <p className={'text header'} style={{
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{info.label}</p>
                        <p className={'muted-text'} style={{
                            fontSize: 12,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }} title={image}>{image}</p>
                    </div>

                    {/* Click on the image to set the pivot; the magenta
                        crosshair shows the current value. Full-size preview is
                        the button in the controls row below. */}
                    <PivotPreview src={getImageUrl(image)} alt={info.label}
                                  fit={'contain'}
                                  style={{height: previewHeight, flexShrink: 0, minHeight: 0}}
                                  imageStyle={checkerStyle}
                                  pivotX={value.offsetX} pivotY={value.offsetY}
                                  subjectBounds={value.trim ?? derivedBounds}
                                  onPickPivot={(x, y) => {
                                      value.offsetX = x;
                                      value.offsetY = y;
                                      stampBounds(image, value);
                                      executeCommand(new UpdateImageCommand(image, value));
                                  }}
                                  onDragPivot={p => setTransientPivot(p ? {key: image, ...p} : undefined)}/>
                {/* Drag the divider to resize the image preview (32–512). */}
                <div onPointerDown={onPreviewResizePointerDown}
                     onPointerMove={onPreviewResizePointerMove}
                     onPointerUp={onPreviewResizePointerUp}
                     onPointerCancel={onPreviewResizePointerUp}
                     title={'Drag to resize the image preview'}
                     className={'layout center'}
                     style={{height: 8, flexShrink: 0, cursor: 'row-resize', margin: '2px 0', touchAction: 'none'}}>
                    <div style={{height: 3, width: 60, borderRadius: 2,
                        backgroundColor: 'rgba(255,255,255,0.25)', pointerEvents: 'none'}}/>
                </div>

                {/* Controls, below the preview. */}
                <div className={'layout vertical gap'} style={{flexShrink: 0, gap: 12}}>
                    <div className={'layout horizontal gap'}>
                        <UploadButton id={image}
                                      confirmBeforeOpen={info.usedBy.length > 1 ? () => confirm({
                                          title: 'Replace shared image?',
                                          body: (
                                              <div className={'layout vertical gap'}>
                                                  <p>This image is used in <strong>{info.usedBy.length}</strong> places —
                                                      replacing it changes all of them:</p>
                                                  {info.usedBy.map((u, i) => <p key={i} className={'muted-text'}>• {u.label}</p>)}
                                              </div>
                                          ),
                                          confirmText: 'Replace everywhere',
                                          danger: true,
                                      }) : undefined}/>
                        <button title={'Full-size preview'} onClick={() => setPreviewImage(image)}>
                            <span className={'fa-solid fa-magnifying-glass-plus'}/>
                        </button>
                        {/* Team colours: derives a mask from the art and uploads
                            it, so it needs the same write access as an upload. */}
                        {canServerWrite && <button
                                title={value.mask
                                    ? 'Edit team colour mask'
                                    : 'Add team colour mask'}
                                disabled={!value.fileName}
                                style={value.mask ? {color: '#7fd1ff'} : undefined}
                                onClick={() => setMaskingImage(image)}>
                            <span className={'fa-solid fa-palette'}/>
                        </button>}
                        {/* remove.bg is a paid server call + re-upload — Tester+. */}
                        {canServerWrite && <button title={'Remove background (AI)'}
                                disabled={!value.fileName}
                                onClick={async () => {
                                    const cutout = await removeBackground(image);
                                    if (!cutout) return;
                                    const url = URL.createObjectURL(cutout);
                                    const proceed = await confirm({
                                        title: 'Remove background',
                                        body: (
                                            <div className={'layout vertical gap'}>
                                                <p>Replace this image with the background removed?</p>
                                                <div className={'layout horizontal gap'} style={{gap: 12}}>
                                                    <div className={'layout vertical center'} style={{gap: 4}}>
                                                        <p className={'muted-text'}>Before</p>
                                                        <img src={getImageUrl(image)} alt={'before'}
                                                             style={{...checkerStyle, width: 150, height: 150, objectFit: 'contain'}}/>
                                                    </div>
                                                    <div className={'layout vertical center'} style={{gap: 4}}>
                                                        <p className={'muted-text'}>After</p>
                                                        <img src={url} alt={'after'}
                                                             style={{...checkerStyle, width: 150, height: 150, objectFit: 'contain'}}/>
                                                    </div>
                                                </div>
                                            </div>
                                        ),
                                        confirmText: 'Replace image',
                                    });
                                    URL.revokeObjectURL(url);
                                    if (proceed) await uploadFile(image, new File([cutout], 'cutout.png', {type: 'image/png'}));
                                }}>
                            <span className={'fa-solid fa-wand-magic-sparkles'}/>
                        </button>}
                        <button onClick={async () => {
                            const proceed = await confirm({
                                title: 'Delete Image',
                                body: (
                                    <div className={'layout vertical gap'}>
                                        <p>Are you sure you want to delete <strong>{info.label}</strong>?</p>
                                        {orphan
                                            ? <p className={'muted-text'}>It is not used by anything.</p>
                                            : <>
                                                <p>It is used by:</p>
                                                {info.usedBy.map((u, i) => <p key={i} className={'muted-text'}>• {u.label}</p>)}
                                                <p>Those references will be left dangling (missing-image fallback).</p>
                                            </>}
                                    </div>
                                ),
                                confirmText: 'Delete',
                                danger: true,
                            });
                            if (proceed) {
                                executeCommand(new RemoveImageCommand(image));
                                setSelectedKey(undefined);
                            }
                        }}>
                            <span className={'fa-solid fa-trash'}/>
                        </button>
                    </div>

                    <div className={'layout vertical gap'}>
                        <div className={'layout horizontal stretch gap flex-grow'} style={{alignItems: 'center', gap: 10}}>
                            <p className={'flex-grow'}>Pivot X,Y</p>
                            <div className={'layout horizontal center'} style={{gap: 6}}>
                                <NumberInput value={value.offsetX} step={'0.01'} displayDecimals={3} style={{width: 83, textAlign: 'right'}} onChange={v => {
                                    value.offsetX = v;
                                    stampBounds(image, value);
                                    executeCommand(new UpdateImageCommand(image, value));
                                }}/>
                                <NumberInput value={value.offsetY} step={'0.01'} displayDecimals={3} style={{width: 83, textAlign: 'right'}} onChange={v => {
                                    value.offsetY = v;
                                    stampBounds(image, value);
                                    executeCommand(new UpdateImageCommand(image, value));
                                }}/>
                            </div>
                        </div>
                        <div className={'layout horizontal center'} style={{gap: 10}}>
                            <p>Scale</p>
                            <input type={'range'} min={SCALE_MIN} max={SCALE_MAX} step={0.01}
                                   value={shownScale}
                                   style={{flexGrow: 1, minWidth: 0}}
                                   onChange={e => previewScale(Number(e.target.value))}
                                   onPointerUp={commitScale}
                                   onKeyUp={commitScale}
                                   onBlur={commitScale}/>
                            <input type={'number'} min={SCALE_MIN} max={SCALE_MAX} step={0.01}
                                   value={scaleFieldValue}
                                   style={{width: 64, flexShrink: 0, textAlign: 'right'}}
                                   onChange={e => onScaleText(e.target.value)}
                                   onBlur={commitScaleText}
                                   onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}/>
                        </div>
                    </div>

                    <div className={'layout vertical'} style={{gap: 4}}>
                        <button className={'layout horizontal center gap'}
                                style={{justifyContent: 'flex-start', gap: 8}}
                                onClick={() => setDetailsOpen(o => !o)}>
                            <span className={`fa-solid ${detailsOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`}/>
                            <span style={{fontWeight: 600}}>Details</span>
                        </button>
                        {detailsOpen && <>
                            <p className={'muted-text'}>
                                Dimensions: {naturalDims ? `${naturalDims.w} × ${naturalDims.h} px` : '—'}
                            </p>
                            <p className={'muted-text'}>
                                Pixels Per Unit: {naturalDims
                                    ? `${computePixelMultiplier(naturalDims.w, shownScale).toFixed(2)} (at scale ${shownScale.toFixed(2)})`
                                    : '—'}
                            </p>
                            {/* What the client crops to when framing this art as a
                                UI icon. Shown for every image: recorded at upload
                                where available, otherwise derived here exactly as
                                the client derives it. */}
                            <div className={'layout horizontal center'} style={{gap: 6}}>
                                <p className={'muted-text'}>
                                    Subject bounds: {(() => {
                                        const bounds = value.trim ?? derivedBounds;
                                        if (!bounds) return '—';
                                        if (!naturalDims) {
                                            return `${(bounds.w * 100).toFixed(0)}% × ${(bounds.h * 100).toFixed(0)}%`;
                                        }
                                        const w = Math.round(bounds.w * naturalDims.w);
                                        const h = Math.round(bounds.h * naturalDims.h);
                                        const x = Math.round(bounds.x * naturalDims.w);
                                        const y = Math.round(bounds.y * naturalDims.h);
                                        return `${w} × ${h} px at (${x}, ${y})`;
                                    })()}
                                    {value.trim
                                        ? ' — recorded'
                                        : derivedBounds ? ' — derived, not yet recorded' : ''}
                                </p>
                            </div>
                            <p style={{fontWeight: 600, marginTop: 6}}>Used by</p>
                            {orphan && <p className={'muted-text'} style={{fontStyle: 'italic'}}>
                                Nothing — this image is unused{info.section === 'unclassified'
                                    ? ' (but may be referenced from client code)' : ''}.
                            </p>}
                            {info.usedBy.map((u, i) =>
                                <p key={i} className={'muted-text'}>• {u.label}</p>)}
                        </>}
                    </div>
                </div>
                </div>
            </div>
        );
    }

    return (
        <div ref={rootRef} className={'layout horizontal'} style={{flexGrow: 1, minHeight: 0}}>
            {/* Main area: the Workshop, always shown. */}
            <div className={'layout vertical'} style={{flexGrow: 1, minWidth: 0, minHeight: 0}}>
                <WorkshopView draggingImageKey={draggingImageKey}
                              transientPivot={transientPivot}
                              transientScale={transientScale}
                              classification={classification}
                              onSelectImage={key => setSelectedKey(key)}
                              onPreviewPivot={(key, x, y) => setTransientPivot({key, x, y})}
                              onCommitPivot={commitWorkshopPivot}
                              onPreviewScale={(key, value) => setTransientScale({key, value})}
                              onCommitScale={commitWorkshopScale}/>
            </div>

            {/* Draggable handle between the Workshop and the sidebar. */}
            <div className={'layout center'}
                 title={'Drag to resize'}
                 onPointerDown={onSidebarResizePointerDown}
                 onPointerMove={onSidebarResizePointerMove}
                 onPointerUp={onSidebarResizePointerUp}
                 onPointerCancel={onSidebarResizePointerUp}
                 style={{width: 8, flexShrink: 0, cursor: 'col-resize', margin: '0 2px', touchAction: 'none'}}>
                <div style={{width: 3, height: 60, borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.25)', pointerEvents: 'none'}}/>
            </div>

            {/* Sidebar: images grid on top, image details below the separator. */}
            <div ref={sidebarRef} className={'layout vertical'}
                 style={{width: sidebarWidth, flexShrink: 0, minHeight: 0}}>
            <div className={'layout vertical'}
                 style={{gap: 10, minWidth: 0, minHeight: 0,
                     // Fixed height while the details pane is shown (the separator
                     // sets it); takes the whole column when details are collapsed.
                     height: showDetails ? imagesHeight : undefined,
                     flexGrow: showDetails ? 0 : 1, flexShrink: showDetails ? 0 : 1}}>
                <div ref={gridScrollRef} className={'layout vertical gap'} style={{flexGrow: 1, overflow: 'auto', height: 0}}>
                    {SECTION_ORDER.map(sectionId => {
                        const items = sections.get(sectionId);
                        if (!items || items.length === 0) return null;
                        const isCollapsed = collapsed.has(sectionId);
                        return (
                            <div key={sectionId} className={'layout vertical gap'}>
                                {/* The sidebar is narrow — the unclassified caveat
                                    goes under the header rather than beside it. */}
                                <button className={'layout horizontal center gap'}
                                        style={{justifyContent: 'flex-start', gap: 8}}
                                        title={sectionId === 'unclassified'
                                            ? 'May be referenced from client code; verify before pruning'
                                            : undefined}
                                        onClick={() => toggleSection(sectionId)}>
                                    <span className={`fa-solid ${isCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'}`}/>
                                    <span style={{fontWeight: 600}}>{SECTION_TITLES[sectionId]}</span>
                                    <span style={{color: 'rgba(255,255,255,0.66)'}}>({items.length})</span>
                                </button>
                                {sectionId === 'unclassified' && !isCollapsed &&
                                    <p className={'muted-text'} style={{fontStyle: 'italic'}}>
                                        May be referenced from client code; verify before pruning
                                    </p>}
                                {!isCollapsed &&
                                    <div className={'layout horizontal gap'}
                                         style={{flexWrap: 'wrap', gap: 4, alignItems: 'flex-start'}}>
                                        {buildRenderUnits(items).map(unit =>
                                            unit.kind === 'single'
                                                ? renderThumbnail(unit.info)
                                                : renderPackGroup(unit))}
                                    </div>}
                            </div>
                        );
                    })}
                    {missing.length > 0 &&
                        <div className={'layout vertical gap'}>
                            <button className={'layout horizontal center gap'}
                                    style={{justifyContent: 'flex-start', gap: 8}}
                                    title={'Images the client will ask for that this branch has none of'}
                                    onClick={() => toggleSection('missing')}>
                                <span className={`fa-solid ${collapsed.has('missing') ? 'fa-chevron-right' : 'fa-chevron-down'}`}/>
                                <span style={{fontWeight: 600}}>Missing</span>
                                <span style={{color: 'rgba(255,255,255,0.66)'}}>({missing.length})</span>
                            </button>
                            {!collapsed.has('missing') &&
                                <p className={'muted-text'} style={{fontStyle: 'italic'}}>
                                    The client will ask for these. In game each uses Master's image if Master
                                    has one, else shows "?". Click one to add it here.
                                </p>}
                            {!collapsed.has('missing') &&
                                <div className={'layout horizontal gap'}
                                     style={{flexWrap: 'wrap', gap: 4, alignItems: 'flex-start'}}>
                                    {missing.map(renderMissing)}
                                </div>}
                        </div>}
                </div>
                {/* Compact toolbar — the sidebar is narrow, so Add/Tidy are
                    icon buttons with tooltips rather than labelled ones. */}
                <div className={'layout horizontal'} style={{gap: 6, alignItems: 'center', flexShrink: 0}}>
                    <input placeholder={'Enter key...'} value={newKey} style={{flexGrow: 1, minWidth: 0}}
                           onChange={v => setNewKey(v.target.value)}
                           onKeyDown={e => { if (e.key === 'Enter') addNewImage(); }}/>
                    <button style={{flexShrink: 0}} title={'Add a new image with this key'} onClick={addNewImage}>
                        <span className={'fa-solid fa-plus'}/>
                    </button>
                    <button style={{flexShrink: 0}}
                            title={'Tidy image packs: collapse redundant duplicate building levels and archive unused entity images (recoverable, no in-game change)'}
                            onClick={tidyImagePacks}>
                        <span className={'fa-solid fa-broom'}/>
                    </button>
                    <div ref={viewPrefsRef} style={{position: 'relative', flexShrink: 0}}>
                        <button title={'View settings'}
                                onClick={() => setShowViewPrefs(!showViewPrefs)}>
                            <span className={'fa-solid fa-sliders'}/>
                        </button>
                        {showViewPrefs &&
                            <div className={'layout vertical box'}
                                 style={{
                                     position: 'absolute', bottom: 'calc(100% + 6px)', right: 0,
                                     width: 280, padding: 12, gap: 10, zIndex: 100,
                                 }}>
                                <p style={{fontWeight: 600}}>View Settings</p>
                                <div className={'layout horizontal center stretch'} style={{gap: 10}}>
                                    <p style={{whiteSpace: 'nowrap'}}>Thumbnail size</p>
                                    <input type={'range'} min={THUMB_MIN} max={THUMB_MAX} value={prefs.thumbSize}
                                           title={`${prefs.thumbSize}px`}
                                           style={{width: 130}}
                                           onChange={e => setPrefs({thumbSize: Number(e.target.value)})}/>
                                </div>
                                <div className={'layout horizontal center stretch'} style={{gap: 10}}>
                                    <p style={{whiteSpace: 'nowrap'}}>Grid size</p>
                                    <input type={'range'} min={8} max={64} step={8} value={prefs.gridSize}
                                           title={`${prefs.gridSize}px`}
                                           style={{width: 130}}
                                           onChange={e => setPrefs({gridSize: Number(e.target.value)})}/>
                                </div>
                                <div className={'layout horizontal center stretch'} style={{gap: 10}}>
                                    <p style={{whiteSpace: 'nowrap'}}>Grid opacity</p>
                                    <input type={'range'} min={0} max={100} value={prefs.gridOpacity}
                                           title={`${prefs.gridOpacity}%`}
                                           style={{width: 130}}
                                           onChange={e => setPrefs({gridOpacity: Number(e.target.value)})}/>
                                </div>
                                <div className={'layout horizontal center stretch'} style={{gap: 10}}>
                                    <p style={{whiteSpace: 'nowrap'}}>Grid colors</p>
                                    <div className={'layout horizontal'} style={{gap: 6}}>
                                        <input type={'color'} value={prefs.gridColorA}
                                               title={'Base color'}
                                               onChange={e => setPrefs({gridColorA: e.target.value})}/>
                                        <input type={'color'} value={prefs.gridColorB}
                                               title={'Checker square color'}
                                               onChange={e => setPrefs({gridColorB: e.target.value})}/>
                                    </div>
                                </div>
                            </div>}
                    </div>
                </div>
            </div>

            {showDetails && <>
                {/* Draggable separator between the images grid and the details pane. */}
                <div className={'layout center'}
                     title={'Drag to resize the images grid'}
                     onPointerDown={onSeparatorPointerDown}
                     onPointerMove={onSeparatorPointerMove}
                     style={{height: 8, flexShrink: 0, cursor: 'row-resize',
                         margin: '2px 0', touchAction: 'none'}}>
                    <div style={{height: 3, width: 60, borderRadius: 2,
                        backgroundColor: 'rgba(255,255,255,0.25)', pointerEvents: 'none'}}/>
                </div>

                {/* Bottom of the sidebar: image details — takes the space the
                    grid's fixed height leaves. */}
                {selected
                    ? renderFlyout(selected)
                    : <div className={'layout vertical box'}
                           style={{flexGrow: 1, minWidth: 0, padding: 12, gap: 12, minHeight: 0, transition: 'none'}}>
                        <div className={'layout horizontal'} style={{justifyContent: 'flex-end'}}>
                            <button title={'Hide image details'} className={'panel-toggle'} onClick={() => setShowDetails(false)}>
                                <span className={'fa-solid fa-chevron-down'}/>
                            </button>
                        </div>
                        <div className={'layout vertical center'} style={{flexGrow: 1}}>
                            <p className={'muted-text'}>No image selected</p>
                        </div>
                    </div>}
            </>}
            {!showDetails &&
                <button title={'Show image details'} className={'panel-toggle'} onClick={() => setShowDetails(true)}
                        style={{border: 'none', margin: 0, alignSelf: 'flex-end', flexShrink: 0}}>
                    {/* Mirror of the pane's "Hide image details" chevron (points
                        the opposite way to indicate expand), borderless. */}
                    <span className={'fa-solid fa-chevron-up'}/>
                </button>}
            </div>

            {previewImage && (
                <div className={'layout vertical'} onClick={() => setPreviewImage(undefined)}
                     style={{
                         position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                         backgroundColor: 'rgba(0, 0, 0, 0.8)',
                         alignItems: 'center', justifyContent: 'center',
                         display: 'flex', zIndex: 1000
                     }}>
                    {/* True pixel scale (1:1), however large or small that
                        is — read-only crosshair. Any click dismisses. */}
                    <PivotPreview src={getImageUrl(previewImage)} alt={previewImage}
                                  fit={'natural'}
                                  imageStyle={checkerStyle}
                                  pivotX={state.images[previewImage]?.offsetX}
                                  pivotY={state.images[previewImage]?.offsetY}/>
                </div>
            )}

            {maskingImage && (
                <TintMaskDialog imageKey={maskingImage}
                                imageUrl={getImageUrl(maskingImage)}
                                checkerStyle={checkerStyle}
                                onClose={() => setMaskingImage(undefined)}/>
            )}
        </div>
    )
}

export default ImagesEditorView;
